import { supabase, isSupabaseConfigured } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────────────────────
// Tarifas de Interrapidísimo (matriz configurable)
// Cada sector tiene una tarifa de kilo inicial + un escalafón de kilo adicional
// (por distancia). El costo de un envío = Kilo Inicial + (kilos extra × escalón).
//   Costo = base + Σ escalones para cada kilo por encima de 1 kg.
//   El 1er kilo extra usa additional[0] (valor por defecto), el 2º additional[1],
//   y así sucesivamente hasta el último escalón disponible.
// ─────────────────────────────────────────────────────────────────────────────

export type ShippingZoneKey =
  | 'local'
  | 'regional'
  | 'national_metro'
  | 'national_municipal'
  | 'difficult'
  | 'veredas';

export interface ShippingZoneRate {
  base: number; // Tarifa kilo inicial
  additional: number[]; // Escalafón de kilo adicional
}

export type ShippingRateMatrix = Record<ShippingZoneKey, ShippingZoneRate>;

export const SHIPPING_ZONE_ORDER: ShippingZoneKey[] = [
  'local',
  'regional',
  'national_metro',
  'national_municipal',
  'difficult',
  'veredas',
];

export const SHIPPING_ZONE_LABELS: Record<ShippingZoneKey, string> = {
  local: 'Sector Local',
  regional: 'Sector Regional',
  national_metro: 'Nacional Metropolitano',
  national_municipal: 'Nacional Municipal',
  difficult: 'Sector Difícil Acceso',
  veredas: 'Sector Veredas',
};

// Valores base iniciales de Interrapidísimo (todos editables desde Configuración).
export const DEFAULT_RATE_MATRIX: ShippingRateMatrix = {
  local: { base: 7900, additional: [3800] },
  regional: { base: 11600, additional: [4400, 5900, 7700] },
  national_metro: { base: 17600, additional: [4900, 6100, 8000, 9200] },
  national_municipal: { base: 20000, additional: [4900, 6100, 8000, 9200] },
  difficult: { base: 31000, additional: [13500, 17300, 22000] },
  veredas: { base: 88000, additional: [15200] },
};

export interface ShippingConfig {
  matrix: ShippingRateMatrix;
  localCities: string[];
  regionalCities: string[];
  nationalMetroCities: string[];
  difficultCities: string[];
  veredasCities: string[];
}

export const DEFAULT_SHIPPING_CONFIG: ShippingConfig = {
  matrix: DEFAULT_RATE_MATRIX,
  localCities: ['Bogotá'],
  regionalCities: [
    'Medellín', 'Cali', 'Barranquilla', 'Bucaramanga', 'Pereira', 'Manizales',
    'Cartagena', 'Ibagué', 'Cúcuta', 'Villavicencio', 'Santa Marta', 'Pasto',
    'Neiva', 'Armenia', 'Valledupar', 'Montería', 'Sincelejo', 'Tunja', 'Popayán',
  ],
  nationalMetroCities: [
    'Riohacha', 'Yopal', 'Quibdó', 'Florencia', 'Arauca', 'Mocoa', 'San Andrés',
  ],
  difficultCities: [],
  veredasCities: [],
};

// Ciudades sugeridas para el selector del checkout / venta directa.
export const COLOMBIA_CITIES: string[] = [
  'Bogotá', 'Medellín', 'Cali', 'Barranquilla', 'Cartagena', 'Cúcuta', 'Bucaramanga',
  'Pereira', 'Santa Marta', 'Ibagué', 'Manizales', 'Villavicencio', 'Pasto', 'Neiva',
  'Armenia', 'Valledupar', 'Montería', 'Sincelejo', 'Tunja', 'Popayán', 'Riohacha',
  'Yopal', 'Quibdó', 'Florencia', 'Leticia', 'Arauca', 'Mocoa', 'San Andrés',
];

// Compara dos matrices de tarifas y devuelve un resumen legible de los sectores
// que cambiaron (para la firma de auditoría). Devuelve null si no cambió nada
// o si no hay matriz previa con la que comparar.
export function diffRateMatrix(
  oldMatrix: ShippingRateMatrix | null | undefined,
  newMatrix: ShippingRateMatrix,
): { oldValue: string; newValue: string } | null {
  if (!oldMatrix) return null;
  const rows: { label: string; before: string; after: string }[] = [];
  SHIPPING_ZONE_ORDER.forEach((zone) => {
    const before = oldMatrix[zone];
    const after = newMatrix[zone];
    if (!before || !after) return;
    const beforeText = `kilo inicial ${before.base}, escalafón [${before.additional.join('/')}]`;
    const afterText = `kilo inicial ${after.base}, escalafón [${after.additional.join('/')}]`;
    if (beforeText !== afterText) {
      rows.push({ label: SHIPPING_ZONE_LABELS[zone], before: beforeText, after: afterText });
    }
  });
  if (rows.length === 0) return null;
  return {
    oldValue: rows.map((r) => `${r.label}: ${r.before}`).join(' | '),
    newValue: rows.map((r) => `${r.label}: ${r.after}`).join(' | '),
  };
}

function norm(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function parseCityList(value: unknown, fallback: string[]): string[] {
  if (typeof value !== 'string' || value.trim().length === 0) return fallback;
  return value
    .split(',')
    .map((c) => c.trim())
    .filter((c) => c.length > 0);
}

function sanitizeRate(raw: unknown, fallback: ShippingZoneRate): ShippingZoneRate {
  if (!raw || typeof raw !== 'object') return { ...fallback };
  const obj = raw as Record<string, unknown>;
  const base = Number(obj.base);
  const additional = Array.isArray(obj.additional)
    ? (obj.additional as unknown[]).map((n) => Number(n)).filter((n) => Number.isFinite(n))
    : fallback.additional;
  return {
    base: Number.isFinite(base) ? base : fallback.base,
    additional: additional.length > 0 ? additional : fallback.additional,
  };
}

function sanitizeMatrix(raw: unknown): ShippingRateMatrix {
  const out = {} as ShippingRateMatrix;
  SHIPPING_ZONE_ORDER.forEach((key) => {
    out[key] = sanitizeRate((raw as Record<string, unknown> | null)?.[key], DEFAULT_RATE_MATRIX[key]);
  });
  return out;
}

// Lee la configuración de envío desde company_settings (whitelist pública).
export async function getShippingConfig(): Promise<ShippingConfig> {
  if (!isSupabaseConfigured || !supabase) return { ...DEFAULT_SHIPPING_CONFIG };
  try {
    const { data, error } = await supabase
      .from('company_settings_public')
      .select(
        'shipping_rate_matrix, shipping_local_cities, shipping_regional_cities, shipping_national_metro_cities, shipping_difficult_cities, shipping_veredas_cities',
      )
      .limit(1)
      .maybeSingle();
    if (error || !data) return { ...DEFAULT_SHIPPING_CONFIG };
    const row = data as Record<string, any>;
    return {
      matrix: row.shipping_rate_matrix
        ? sanitizeMatrix(row.shipping_rate_matrix)
        : { ...DEFAULT_RATE_MATRIX },
      localCities: parseCityList(row.shipping_local_cities, DEFAULT_SHIPPING_CONFIG.localCities),
      regionalCities: parseCityList(row.shipping_regional_cities, DEFAULT_SHIPPING_CONFIG.regionalCities),
      nationalMetroCities: parseCityList(
        row.shipping_national_metro_cities,
        DEFAULT_SHIPPING_CONFIG.nationalMetroCities,
      ),
      difficultCities: parseCityList(row.shipping_difficult_cities, DEFAULT_SHIPPING_CONFIG.difficultCities),
      veredasCities: parseCityList(row.shipping_veredas_cities, DEFAULT_SHIPPING_CONFIG.veredasCities),
    };
  } catch (err) {
    console.warn('[shipping] config fetch failed:', err);
    return { ...DEFAULT_SHIPPING_CONFIG };
  }
}

// Clasifica una ciudad en uno de los 6 sectores. Lo que no cae en ninguna lista
// queda como "Nacional Municipal" (la cobertura por defecto).
export function classifyZone(city: string, config: ShippingConfig): ShippingZoneKey {
  const c = norm(city);
  if (!c) return 'national_municipal';
  if (config.veredasCities.some((x) => norm(x) === c)) return 'veredas';
  if (config.difficultCities.some((x) => norm(x) === c)) return 'difficult';
  if (config.localCities.some((x) => norm(x) === c)) return 'local';
  if (config.regionalCities.some((x) => norm(x) === c)) return 'regional';
  if (config.nationalMetroCities.some((x) => norm(x) === c)) return 'national_metro';
  return 'national_municipal';
}

// Tarifa adicional aplicable para el kilo extra #extraIndex (0-based). Si el
// escalafón se queda corto, se repite el último valor disponible.
export function additionalRateFor(zone: ShippingZoneKey, config: ShippingConfig, extraIndex: number): number {
  const tiers = config.matrix[zone].additional;
  if (tiers.length === 0) return 0;
  const idx = Math.min(extraIndex, tiers.length - 1);
  return tiers[idx];
}

// Costo de envío por peso y sector:
//   Costo = Kilo Inicial + Σ escalones por cada kilo por encima de 1 kg.
export function computeWeightedShippingCost(
  totalWeightKg: number,
  zone: ShippingZoneKey,
  config: ShippingConfig,
): number {
  const rate = config.matrix[zone];
  const weight = Math.max(0, totalWeightKg);
  // El primer kilo (incluye fracción) siempre usa la tarifa de kilo inicial.
  const extraKg = Math.max(0, Math.ceil(weight) - 1);
  let cost = rate.base;
  for (let i = 0; i < extraKg; i++) {
    cost += additionalRateFor(zone, config, i);
  }
  return cost;
}

// Costo de flete por ciudad (compatibilidad). Si no se indica peso, se asume 1 kg.
export function computeShippingCost(
  city: string,
  config: ShippingConfig,
  totalWeightKg = 1,
): number {
  return computeWeightedShippingCost(totalWeightKg, classifyZone(city, config), config);
}

// Costo de flete efectivo de un pedido: 0 si no requiere envío (línea premium
// incluye el flete en el precio), o el valor por peso/sector de Interrapidísimo.
export function resolveOrderShippingCost(
  city: string,
  requiresShipping: boolean,
  config: ShippingConfig,
  totalWeightKg = 1,
): number {
  if (!requiresShipping) return 0;
  return computeShippingCost(city, config, totalWeightKg);
}

// Suma el peso de una lista de ítems. `onlyChargeable` limita la suma a los
// productos de línea sencilla (los premium solo se muestran como informativos).
export interface WeighableItem {
  weightKg: number;
  quantity: number;
  chargeShipping: boolean;
}

export function sumItemsWeight(items: WeighableItem[], onlyChargeable = false): number {
  return items.reduce((sum, it) => {
    if (onlyChargeable && !it.chargeShipping) return sum;
    return sum + Math.max(0, it.weightKg) * Math.max(0, it.quantity);
  }, 0);
}